import OfficeGroupEnum from '../../Enums/OfficeGroupEnum';

export default class OfficeHelper {
  // The one cache of the office list (with office groups): OfficeService.GetOfficesForSelect fills it on its first
  // call and answers every later call from it. app.js renders the app only once it has loaded, so these helpers can
  // be called synchronously. It lives here rather than in OfficeService because this helper is imported by
  // ValidityHelper, and importing OfficeService (-> OfficeItem -> panels using ValidityHelper in static fields) would
  // make that an import cycle. Office ids may be numbers or strings.
  static offices = null;

  static SetOffices(offices) {
    OfficeHelper.offices = Array.isArray(offices) ? offices : [];
  }

  static IsLoaded() {
    return OfficeHelper.offices !== null;
  }

  static GetOffices() {
    return OfficeHelper.offices ?? [];
  }

  static GetOfficeGroupId(officeId) {
    if (officeId === null || officeId === undefined || officeId === '') {
      return null;
    }
    const office = OfficeHelper.GetOffices().find((o) => o.officeId.toString() === officeId.toString());
    return office?.officeGroupId ?? null;
  }

  static GetOfficeIdsOfOfficeGroup(officeGroupId, asString) {
    return OfficeHelper.GetOffices()
      .filter((o) => o.officeGroupId === officeGroupId)
      .map((o) => (asString ? o.officeId.toString() : o.officeId));
  }

  // The offices of the EU Offices office group (Belgium, Hungary, Spain).
  static GetEUOffices(asString) {
    return OfficeHelper.GetOfficeIdsOfOfficeGroup(OfficeGroupEnum.Europe, asString);
  }

  static IsEUOffice(officeId) {
    return OfficeHelper.GetOfficeGroupId(officeId) === OfficeGroupEnum.Europe;
  }

  // True for two offices of the same office group; an office is always in the same group as itself.
  static IsSameOfficeGroup(officeId, otherOfficeId) {
    if (officeId === otherOfficeId) {
      return true;
    }
    const officeGroupId = OfficeHelper.GetOfficeGroupId(officeId);
    return officeGroupId !== null && officeGroupId === OfficeHelper.GetOfficeGroupId(otherOfficeId);
  }

  // The office groups of the given offices (by default the whole office list), each once and in the order of the
  // list: { officeGroupId, name }.
  static GetOfficeGroups(offices = OfficeHelper.GetOffices()) {
    const officeGroups = [];
    offices.forEach((office) => {
      if (!officeGroups.some((officeGroup) => officeGroup.officeGroupId === office.officeGroupId)) {
        officeGroups.push({ officeGroupId: office.officeGroupId, name: office.officeGroupName });
      }
    });
    return officeGroups;
  }

  // The offices of the given office groups, in the order of the office list; string ids when asString.
  static GetOfficeIdsOfOfficeGroups(officeGroupIds, asString, offices = OfficeHelper.GetOffices()) {
    const officeGroupIdTexts = officeGroupIds.map((officeGroupId) => `${officeGroupId}`);
    return offices
      .filter((office) => officeGroupIdTexts.includes(`${office.officeGroupId}`))
      .map((office) => (asString ? office.officeId.toString() : office.officeId));
  }

  // The office groups the given offices belong to, each once.
  static GetOfficeGroupIdsOfOffices(officeIds, offices = OfficeHelper.GetOffices()) {
    const officeIdTexts = officeIds.map((officeId) => `${officeId}`);
    const selectedOffices = offices.filter((office) => officeIdTexts.includes(`${office.officeId}`));
    return OfficeHelper.GetOfficeGroups(selectedOffices).map((officeGroup) => officeGroup.officeGroupId);
  }

  // True when the offices make up whole office groups: every office of each group they touch is among them. An empty
  // selection counts as whole.
  static IsWholeGroupSelection(officeIds, offices = OfficeHelper.GetOffices()) {
    const officeIdTexts = officeIds.map((officeId) => `${officeId}`);
    const officeGroupIds = OfficeHelper.GetOfficeGroupIdsOfOffices(officeIds, offices);
    return OfficeHelper.GetOfficeIdsOfOfficeGroups(officeGroupIds, true, offices).every((officeId) =>
      officeIdTexts.includes(officeId)
    );
  }

  // Whether an office-or-group search input shows its value as office groups (rather than as single offices): the
  // value is empty or made of whole groups, and of one group at most for a single select. A saved search therefore
  // reopens the way it was made.
  static OpensInGroupMode(officeIds, isSingleSelect, offices = OfficeHelper.GetOffices()) {
    const isWithinOneGroup = OfficeHelper.GetOfficeGroupIdsOfOffices(officeIds, offices).length <= 1;
    return OfficeHelper.IsWholeGroupSelection(officeIds, offices) && (!isSingleSelect || isWithinOneGroup);
  }

  // The office ids of an office search input's value: a list stays a list, a single id from an older saved search
  // becomes a one-item list, and an empty value (the eraser sets '') an empty list.
  static ToOfficeIdList(value) {
    if (Array.isArray(value)) {
      return value;
    }
    return value === null || value === undefined || value === '' ? [] : [value];
  }

  // Centralized default for office multi-selects: every office of the seed office's office group (just the office
  // itself when the group is its own). Keeps the caller's id type (string vs number). A user without an office gets no
  // default office, as an empty list: a missing id inside the list would fail the backend's office id list.
  static GetDefaultOfficeIds(officeId) {
    if (officeId === null || officeId === undefined || officeId === '' || Number.isNaN(officeId)) {
      return [];
    }
    const officeGroupId = OfficeHelper.GetOfficeGroupId(officeId);
    if (officeGroupId === null) {
      return [officeId];
    }
    return OfficeHelper.GetOfficeIdsOfOfficeGroup(officeGroupId, typeof officeId === 'string');
  }
}
